<template>
  <div class="campaign-data-page">
    <div class="campaign-data-inner">
    <CCard class="campaign-panel">
      <CCardBody class="campaign-panel__body">
        <h5 class="campaign-panel__title">Campaign Data</h5>
        <p class="campaign-panel__subtitle">
          Choose a campaign and date range, then load results.
        </p>
        <div class="campaign-filters">
          <div class="campaign-filter-cell">
            <label class="campaign-label">Campaign</label>
            <div class="campaign-control-slot">
              <CSelect
                class="campaign-select"
                :options="campaignSelectOptions"
                :value.sync="selectedCampaign"
                :disabled="loadingCampaigns"
              />
            </div>
            <small v-if="loadingCampaigns" class="campaign-help">Loading campaigns…</small>
          </div>
          <div class="campaign-filter-cell">
            <label class="campaign-label">Date range</label>
            <div class="campaign-ctk-root campaign-control-slot campaign-control-slot--date">
              <VueCtkDateTimePicker
                :overlay="false"
                :range="true"
                :only-date="true"
                :no-label="true"
                :right="true"
                label="Select date range"
                id="CampaignDataDateRange"
                format="YYYY-MM-DD"
                formatted="ll"
                color="#1890ff"
                v-model="dateRange"
                :max-date="maxDate"
              />
            </div>
          </div>
          <div class="campaign-filter-cell campaign-filter-cell--submit">
            <span class="campaign-label campaign-label--spacer" aria-hidden="true">Date range</span>
            <div class="campaign-control-slot campaign-control-slot--btn">
              <CButton
                color="primary"
                block
                class="campaign-btn-submit"
                :disabled="submitDisabled"
                @click="submitFetch"
              >
                <span
                  v-if="loadingData"
                  class="spinner-border spinner-border-sm mr-1"
                  role="status"
                />
                Submit
              </CButton>
            </div>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="campaign-panel campaign-panel--results">
      <CCardBody class="campaign-panel__body campaign-results-body">
        <div class="campaign-results-head">
          <span class="campaign-results-head__title">Results</span>
          <span v-if="resultMetaText" class="campaign-meta-pill">{{ resultMetaText }}</span>
        </div>
        <div v-if="loadingData" class="campaign-results-state">
          <span class="spinner-border campaign-spinner" role="status" />
          <p class="campaign-results-state__text">Loading campaign data…</p>
        </div>
        <div
          v-else-if="showEmptyState"
          class="campaign-empty text-center"
        >
          <CIcon name="cil-file" class="campaign-empty__icon mb-2" />
          <p class="campaign-empty__title">No Data Found</p>
          <p class="campaign-empty__hint">No rows were returned for this campaign and date range.</p>
        </div>
        <div v-else class="campaign-table-wrap">
          <CDataTable
            class="campaign-simple-table"
            hover
            small
            :items="tableItems"
            :fields="tableFields"
            :items-per-page="15"
            pagination
            :sorter="true"
          />
        </div>
      </CCardBody>
    </CCard>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const FALLBACK_API_BASE = 'https://developer.quizpro.gamehubbd.com/api/v2.0/admin';

function apiBase() {
  const raw =
    process.env.VUE_APP_CAMPAIGN_API_BASE ||
    process.env.VUE_APP_ADMIN_URL ||
    FALLBACK_API_BASE;
  return String(raw).replace(/\/+$/, '');
}

function extractArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.campaigns)) return payload.campaigns;
  if (payload.data && typeof payload.data === 'object' && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
}

function normalizeCampaignOptions(rawList) {
  return rawList
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'string' || typeof item === 'number') {
        const v = String(item).trim();
        return v ? { value: v, label: v } : null;
      }
      if (typeof item === 'object') {
        const value =
          item.campaign ??
          item.slug ??
          item.name ??
          item.code ??
          item.id ??
          item.value;
        if (value == null || String(value).trim() === '') return null;
        const str = String(value).trim();
        const label =
          item.label ??
          item.title ??
          item.display_name ??
          item.name ??
          str;
        return { value: str, label: String(label) };
      }
      return null;
    })
    .filter(Boolean);
}

function extractRows(serverBody) {
  const d = serverBody;
  if (d == null) return [];
  if (Array.isArray(d)) return d;
  if (typeof d !== 'object') return [];
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === 'object' && Array.isArray(d.data.rows)) return d.data.rows;
  if (Array.isArray(d.rows)) return d.rows;
  if (Array.isArray(d.items)) return d.items;
  return [];
}

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function scalarForCell(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch (e) {
      return String(val);
    }
  }
  return String(val);
}

export default {
  name: 'CampaignData',
  data() {
    return {
      loadingCampaigns: false,
      loadingData: false,
      campaignSelectOptions: [{ value: null, label: 'Select campaign' }],
      selectedCampaign: null,
      dateRange: {
        start: null,
        end: null,
      },
      maxDate: moment().format('YYYY-MM-DD'),
      tableItems: [],
      tableFields: [],
      hasSubmitted: false,
      resultSummary: null,
    };
  },
  computed: {
    showEmptyState() {
      return this.hasSubmitted && !this.loadingData && this.tableItems.length === 0;
    },
    resultMetaText() {
      if (!this.hasSubmitted || this.loadingData) return '';
      const c = this.resultSummary && this.resultSummary.campaign;
      const s = this.resultSummary && this.resultSummary.startDate;
      const e = this.resultSummary && this.resultSummary.endDate;
      if (!c || !s || !e) return '';
      return `${c} · ${s} → ${e}`;
    },
    submitDisabled() {
      return (
        this.loadingData ||
        this.loadingCampaigns ||
        !this.selectedCampaign ||
        !this.dateRange ||
        !this.dateRange.start ||
        !this.dateRange.end
      );
    },
  },
  mounted() {
    this.fetchDistinctCampaigns();
  },
  methods: {
    async fetchDistinctCampaigns() {
      this.loadingCampaigns = true;
      const base = apiBase();
      try {
        const { data } = await this.$axios.get(`${base}/distinct-campaign`);
        const list = extractArray(data);
        const opts = normalizeCampaignOptions(list);
        this.campaignSelectOptions = [{ value: null, label: 'Select campaign' }, ...opts];
      } catch (e) {
        console.error(e);
        this.$swal('Error', 'Could not load campaigns. Check API configuration or network.', 'error');
        this.campaignSelectOptions = [{ value: null, label: 'Select campaign' }];
      } finally {
        this.loadingCampaigns = false;
      }
    },
    submitFetch() {
      if (this.submitDisabled) {
        this.$swal('Missing information', 'Please select a campaign and a full date range.', 'info');
        return;
      }
      this.loadCampaignData();
    },
    async loadCampaignData() {
      const base = apiBase();
      const startDate = moment(this.dateRange.start).format('YYYY-MM-DD');
      const endDate = moment(this.dateRange.end).format('YYYY-MM-DD');
      if (
        !moment(startDate, 'YYYY-MM-DD', true).isValid() ||
        !moment(endDate, 'YYYY-MM-DD', true).isValid()
      ) {
        this.$swal('Invalid dates', 'Please pick a valid date range.', 'warning');
        return;
      }
      const params = {
        campaign: this.selectedCampaign,
        startDate,
        endDate,
      };
      this.loadingData = true;
      this.hasSubmitted = true;
      this.tableItems = [];
      this.tableFields = [];
      try {
        const { data } = await this.$axios.get(`${base}/campaign-data`, { params });
        const rows = extractRows(data);
        const normalized = Array.isArray(rows) ? rows : [];
        this.resultSummary = {
          campaign: params.campaign,
          startDate: params.startDate,
          endDate: params.endDate,
        };
        this.buildTable(normalized);
      } catch (e) {
        console.error(e);
        const msg =
          (e && e.data && e.data.message) ||
          (e && e.message) ||
          'Failed to load campaign data.';
        this.$swal('Error', msg, 'error');
        this.tableItems = [];
        this.tableFields = [];
      } finally {
        this.loadingData = false;
      }
    },
    buildTable(rows) {
      if (!rows.length) {
        this.tableItems = [];
        this.tableFields = [];
        return;
      }
      const first = rows[0];
      if (typeof first !== 'object' || first === null) {
        this.tableItems = rows.map((cell, i) => ({
          key: `row-${i}`,
          value: scalarForCell(cell),
        }));
        this.tableFields = [
          { key: 'value', label: 'Value', sortable: true },
        ];
        return;
      }
      const keys = Object.keys(first).filter((k) => k !== 'key');
      const orderedKeys = keys.sort();
      this.tableFields = orderedKeys.map((k) => {
        const label = humanizeKey(k);
        const alignRight =
          /^(total|count|amount|sum)$/i.test(k) ||
          /^total$/i.test(String(label).replace(/\s+/g, ''));
        return {
          key: k,
          label,
          sortable: true,
          ...(alignRight ? { _classes: 'text-right campaign-col-num' } : {}),
        };
      });
      this.tableItems = rows.map((row, i) => {
        const flat = {};
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          orderedKeys.forEach((k) => {
            flat[k] = scalarForCell(row[k]);
          });
        } else {
          flat.value = scalarForCell(row);
        }
        flat._rowKey =
          row && row.id != null
            ? `id-${row.id}`
            : row && row.key != null
              ? String(row.key)
              : `row-${i}`;
        return flat;
      });
      if (!orderedKeys.length && rows.length) {
        this.tableFields = [{ key: 'value', label: 'Value', sortable: true }];
      }
    },
  },
};
</script>

<style scoped>
.campaign-data-page {
  background: #f5f7f9;
  margin: -1rem -15px 0;
  padding: 1.25rem 15px 2rem;
}

.campaign-data-inner {
  max-width: 920px;
  margin: 0 auto;
  overflow: visible;
}

.campaign-panel {
  border: none;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.25rem;
  /* must stay visible so vue-ctk calendar / overlay is not clipped */
  overflow: visible;
}

.campaign-panel--results {
  margin-bottom: 0;
}

.campaign-panel__body {
  padding: 1.35rem 1.5rem 1.5rem;
  overflow: visible;
}

.campaign-panel__title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #262626;
  margin: 0 0 0.35rem;
  letter-spacing: -0.01em;
}

.campaign-panel__subtitle {
  font-size: 0.8125rem;
  color: #8c8c8c;
  margin: 0 0 1.5rem;
  line-height: 1.45;
}

.campaign-filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 1rem;
  align-items: start;
}

@media (min-width: 768px) {
  .campaign-filters {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.35fr) minmax(132px, 160px);
    column-gap: 1rem;
    row-gap: 0;
    align-items: start;
  }
}

.campaign-filter-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.campaign-control-slot {
  display: flex;
  align-items: center;
  min-height: 40px;
  width: 100%;
}

.campaign-control-slot--date {
  align-items: stretch;
}

.campaign-control-slot--btn {
  min-height: 40px;
}

.campaign-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8c8c8c;
  margin-bottom: 0.5rem;
  line-height: 1.25;
  min-height: calc(0.6875rem * 1.25 + 0.05rem);
}

.campaign-label--spacer {
  visibility: hidden;
  user-select: none;
}

.campaign-help {
  display: block;
  margin-top: 0.35rem;
  color: #8c8c8c;
  font-size: 0.75rem;
}

/* Remove extra bottom margin from CoreUI CFormGroup inside CSelect */
.campaign-select >>> .form-group {
  margin-bottom: 0 !important;
}

.campaign-select {
  width: 100%;
  flex: 1;
  min-width: 0;
}

.campaign-select >>> select.form-control,
.campaign-select >>> .custom-select {
  border-radius: 6px;
  border-color: #d9d9d9;
  height: 40px;
  min-height: 40px;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-size: 0.9375rem;
  color: #262626;
}

.campaign-control-slot--date {
  width: 100%;
}

.campaign-control-slot--date >>> .date-time-picker {
  width: 100%;
  max-width: 100%;
}

.campaign-ctk-root {
  position: relative;
  z-index: 1;
  overflow: visible !important;
}

/* vue-ctk defaults (z-index 9 / 999) sit under CoreUI sidebar (~1019+); raise when open */
.campaign-ctk-root >>> .datetimepicker.visible {
  z-index: 10050 !important;
}

.campaign-ctk-root >>> .datetimepicker .datepicker {
  z-index: 10051 !important;
}

.campaign-ctk-root >>> .time-picker-overlay {
  z-index: 10049 !important;
}

.campaign-control-slot--date >>> .field-input,
.campaign-control-slot--date >>> input.form-control,
.campaign-control-slot--date >>> .input-field {
  border-radius: 6px !important;
  border-color: #d9d9d9 !important;
  height: 40px !important;
  min-height: 40px !important;
  font-size: 0.9375rem !important;
  line-height: 1.4 !important;
}

.campaign-btn-submit {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  border-radius: 6px !important;
  background-color: #1890ff !important;
  border-color: #1890ff !important;
  color: #fff !important;
}

.campaign-control-slot--btn .campaign-btn-submit {
  width: 100%;
}

.campaign-btn-submit:hover:not(:disabled),
.campaign-btn-submit:focus:not(:disabled) {
  background-color: #40a9ff !important;
  border-color: #40a9ff !important;
  color: #fff !important;
}

.campaign-btn-submit:disabled {
  opacity: 0.55;
}

.campaign-results-body {
  padding-top: 1rem;
  min-height: 200px;
}

.campaign-results-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.campaign-results-head__title {
  font-size: 1rem;
  font-weight: 600;
  color: #262626;
}

.campaign-meta-pill {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #096dd9;
  background: #e6f7ff;
  border-radius: 999px;
  border: 1px solid #91d5ff;
  max-width: 100%;
  word-break: break-word;
}

.campaign-results-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

.campaign-spinner {
  color: #1890ff;
  width: 2rem;
  height: 2rem;
}

.campaign-results-state__text {
  margin: 1rem 0 0;
  font-size: 0.875rem;
  color: #8c8c8c;
}

.campaign-empty {
  padding: 2.5rem 1.25rem;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  color: #8c8c8c;
}

.campaign-empty__icon {
  font-size: 2rem;
  opacity: 0.4;
}

.campaign-empty__title {
  font-size: 1rem;
  font-weight: 600;
  color: #595959;
  margin: 0 0 0.35rem;
}

.campaign-empty__hint {
  font-size: 0.8125rem;
  margin: 0;
  color: #8c8c8c;
}

.campaign-table-wrap {
  margin: 0 -0.25rem;
}

.campaign-simple-table {
  margin: 0;
}

.campaign-simple-table >>> .table {
  margin-bottom: 0;
  border: none;
  color: #434343;
}

.campaign-simple-table >>> .table thead th {
  border-top: none;
  border-bottom: 1px solid #f0f0f0;
  border-left: none;
  border-right: none;
  background: #fff;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #595959;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
}

.campaign-simple-table >>> .table tbody td {
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  font-size: 0.9375rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.campaign-simple-table >>> .table tbody tr:last-child td {
  border-bottom: none;
}

.campaign-simple-table >>> .table tbody tr:hover {
  background: #fafafa;
}

.campaign-simple-table >>> tr.table-sm td,
.campaign-simple-table >>> tr.table-sm th {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.campaign-simple-table >>> .table-responsive {
  border: none;
}

.campaign-simple-table >>> .row.my-2.mx-0 {
  margin-bottom: 0.75rem !important;
}

.campaign-simple-table >>> .row.my-2.mx-0 input.form-control {
  border-radius: 6px;
  border-color: #d9d9d9;
}

.campaign-simple-table >>> .pagination {
  margin-top: 1rem;
  margin-bottom: 0;
}

.campaign-col-num {
  font-variant-numeric: tabular-nums;
}
</style>
